import type { BrandDocument } from '@models/brand';
import type { ProductDocument } from 'models/product';
import Link from 'next/link';
import { slugify } from '@util/urlHelpers';
import sanitize from 'sanitize-html';

const BikeDetails = ({
  product,
}: {
  product: ProductDocument;
  brand?: BrandDocument | null;
}) => {
  const tabs = [
    {
      id: 'details',
      title: 'Product Details',
      shortTitle: 'Details',
      content: (
        <div className="py-8">
          <h3 className="mb-[0.4rem] text-[1.6rem] font-semibold">Overview</h3>

          <div className="mt-6 flex w-full flex-col whitespace-nowrap">
            <div className="mb-[0.4rem] flex">
              <p className="w-[10rem] text-[1.6rem] font-medium">Condition:</p>
              <Link
                href={`/${product.condition?.toLowerCase()}-dirt-bikes-for-sale`}
                className="w-fit font-semibold text-brand-secondary"
              >
                {product.condition || 'N/A'}
              </Link>
            </div>

            <div className="mb-[0.4rem] flex">
              <p className="w-[10rem] text-[1.6rem] font-medium">Make:</p>
              <Link
                className="w-fit font-semibold text-brand-secondary"
                href={`/${product.make
                  ?.toLowerCase()
                  .replaceAll(' ', '-')}-dirt-bikes-for-sale/`}
              >
                {product.make}
              </Link>
            </div>
            <div className="mb-[0.4rem] flex">
              <p className="w-[10rem] text-[1.6rem] font-medium">Year:</p>
              <Link
                className="w-fit font-semibold text-brand-secondary"
                href={`/${product.year}-dirt-bikes-for-sale`}
              >
                {product.year || 'N/A'}
              </Link>
            </div>
            <div className="mb-[0.4rem] flex">
              <p className="w-[10rem] text-[1.6rem] font-medium">Model:</p>
              <Link
                href={`/${slugify(product.model ?? '')}-dirt-bikes-for-sale`}
                className="w-fit font-semibold text-brand-secondary"
              >
                {product.model || 'N/A'}
              </Link>
            </div>
            <div className="mb-[0.4rem] flex">
              <p className="w-[10rem] text-[1.6rem] font-medium">Location:</p>
              <Link
                className="w-fit font-semibold text-brand-secondary"
                href={`/dirt-bikes-for-sale${
                  product.location_display
                    ? `-near-${slugify(product.location_display)}`
                    : ''
                }`}
              >
                {product.location_display || 'N/A'}
              </Link>
            </div>
          </div>

          <div className="mt-6">
            <div>
              {product.description_html ? (
                <>
                  {/* Editor content class so it matches what people saw in the create listing tool  */}
                  <div
                    className={`editor-content overflow-x-auto text-[1.6rem] [&>ul]:list-inside [&>ul]:list-disc`}
                    dangerouslySetInnerHTML={{
                      __html: sanitize(product.description_html, {
                        allowedTags: sanitize.defaults.allowedTags.filter(
                          (tag) => tag !== 'a'
                        ),
                      }),
                    }}
                  />
                </>
              ) : product.description ? (
                <>
                  <p
                    className={
                      'overflow-x-auto whitespace-pre-line text-[1.6rem]'
                    }
                  >
                    {product.description}
                  </p>
                </>
              ) : (
                <span className="italic"> No description available</span>
              )}
            </div>
          </div>
        </div>
      ),
    },
  ] as const;

  return (
    <div className="px-8 sm:px-0">
      <div className="m-0 overflow-x-hidden pb-4">
        {tabs.map((tab, index) => (
          <input
            key={tab.id}
            type="radio"
            id={`tab-${tab.id}`}
            name="bike-tabs"
            defaultChecked={index === 0}
            className="hidden"
          />
        ))}

        <ul className="m-0 flex list-none items-stretch border-b border-zinc-300 p-0">
          {tabs.map((tab) => (
            <li key={tab.id}>
              <label
                htmlFor={`tab-${tab.id}`}
                className="-mb-px block w-fit flex-shrink-0 flex-grow cursor-pointer whitespace-nowrap border border-b border-r-0 border-zinc-300 bg-zinc-100 p-4 text-center text-base font-semibold text-black transition-all duration-200 last:border-r hover:bg-zinc-200 sm:p-4"
              >
                <span className="hidden text-[1.8rem] sm:inline">
                  {tab.title}
                </span>
                <span className="text-[1.8rem] sm:hidden">
                  {tab.shortTitle || tab.title}
                </span>
              </label>
            </li>
          ))}
        </ul>

        {tabs.map((tab) => (
          <div key={tab.id} className="hidden px-4 pb-4">
            {tab.content}
          </div>
        ))}
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
          input[type="radio"]:nth-of-type(1):checked ~ ul li:nth-of-type(1) label {
            border-left: 1px solid #d4d4d8 !important;
            border-right: 1px solid #d4d4d8 !important;
            border-top: 1px solid #d4d4d8 !important;
            border-bottom: none !important;
            background: transparent !important;
            color: black !important;
          }

          input[type="radio"]:nth-of-type(1):checked ~ div:nth-of-type(1) {
            display: block !important;
          }
        `,
        }}
      />
    </div>
  );
};

export default BikeDetails;
